import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTimer } from '../lib/TimerContext';
import {
  getClients, getProjects, getTasks,
  getRevenueSummary, getUpcomingTasks, getDashboardAlerts,
  getMonthlySummary, getUserCapacity, getProjectProfitability,
  createTask, createProject, createClient,
} from '../lib/api';
import { formatTime, formatCurrency, getMonthName } from '../lib/utils';
import TaskDetailDialog from '../components/TaskDetailDialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Users, FolderKanban, CheckSquare, Clock,
  TrendingUp, TrendingDown, ArrowRight, Play,
  Calendar, AlertCircle, AlertTriangle, XCircle,
  DollarSign, Target, Activity, Plus, Zap,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { toast } from 'sonner';

const MONTH_NOW = (() => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
})();

// ─── helpers ────────────────────────────────────────────────────────────────

const getMarginColor = (m) => m >= 30 ? '#22c55e' : m >= 15 ? '#f59e0b' : '#ef4444';
const getProfitColor = (p) => p > 0 ? '#22c55e' : p === 0 ? '#6b7280' : '#ef4444';

// ─── sub-components ──────────────────────────────────────────────────────────

const AlertBox = ({ alerts, missingData, onAlertClick }) => {
  const all = [...(alerts || [])];
  if (missingData?.users_missing_hourly_rate?.length > 0)
    all.unshift({ type: 'missing_data', severity: 'warning', title: 'Chybí hodinová sazba', description: `Uživatelé: ${missingData.users_missing_hourly_rate.join(', ')}`, entity_type: 'users' });
  if (missingData?.users_missing_capacity?.length > 0)
    all.unshift({ type: 'missing_data', severity: 'warning', title: 'Chybí měsíční kapacita', description: `Uživatelé: ${missingData.users_missing_capacity.join(', ')}`, entity_type: 'users' });
  if (all.length === 0) return null;
  return (
    <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
      <h2 className="font-oswald text-xl font-bold uppercase tracking-tight flex items-center gap-2 mb-4">
        <AlertCircle size={20} className="text-red-600" /> Varování ({all.length})
      </h2>
      <div className="space-y-2 max-h-56 overflow-y-auto">
        {all.slice(0, 10).map((a, i) => (
          <div key={i} onClick={() => onAlertClick(a)}
            className={`p-3 border-2 cursor-pointer transition-colors ${a.severity === 'error' ? 'border-red-400 bg-red-50 hover:border-red-500' : 'border-orange-300 bg-orange-50 hover:border-orange-400'}`}>
            <div className="flex items-start gap-3">
              {a.severity === 'error' ? <XCircle size={16} className="text-red-600 mt-0.5" /> : <AlertTriangle size={16} className="text-orange-500 mt-0.5" />}
              <div className="flex-1 min-w-0">
                <div className="font-oswald text-sm font-bold truncate">{a.title}</div>
                <div className="text-xs text-gray-600 font-serif truncate">{a.description}</div>
              </div>
              <ArrowRight size={14} className="text-gray-400 flex-shrink-0 mt-1" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const KPICard = ({ title, value, subtitle, icon: Icon, color }) => (
  <div className="bg-white border-2 border-black p-5 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="font-oswald text-xs uppercase tracking-wide text-gray-500 mb-1">{title}</div>
        <div className="font-oswald text-2xl font-bold" style={{ color: color || 'inherit' }}>{value}</div>
        {subtitle && <div className="text-xs text-gray-500 font-serif mt-1">{subtitle}</div>}
      </div>
      <div className="p-3 border-2 border-black" style={{ backgroundColor: color || '#F4E600' }}>
        <Icon size={20} className="text-black" />
      </div>
    </div>
  </div>
);

const StatCard = ({ title, value, icon: Icon, link, color = '#F4E600' }) => (
  <Link to={link} className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all">
    <div className="flex items-start justify-between">
      <div>
        <div className="font-oswald text-xs uppercase tracking-wide text-gray-500 mb-1">{title}</div>
        <div className="font-oswald text-3xl font-bold">{value}</div>
      </div>
      <div className="p-3 border-2 border-black" style={{ backgroundColor: color }}>
        <Icon size={24} className="text-black" />
      </div>
    </div>
    <div className="mt-4 flex items-center text-sm font-oswald uppercase tracking-wide text-gray-600">
      Zobrazit detail <ArrowRight size={14} className="ml-2" />
    </div>
  </Link>
);

const CapacityBar = ({ users }) => {
  if (!users?.length) return null;
  return (
    <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
      <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
        <Activity size={20} /> Kapacita týmu
      </h2>
      <div className="space-y-3">
        {users.slice(0, 6).map((u) => {
          const barColor = u.status_color === 'red' ? '#ef4444' : u.status_color === 'yellow' ? '#f59e0b' : '#22c55e';
          return (
            <div key={u.user_id} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="font-oswald truncate">{u.user_name}</span>
                <span className={`font-bold ${u.status === 'overloaded' ? 'text-red-600' : u.status === 'warning' ? 'text-orange-500' : 'text-green-600'}`}>
                  {u.usage_percent}%
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, u.usage_percent)}%`, backgroundColor: barColor }} />
              </div>
              <div className="text-xs text-gray-500 font-serif">{u.used_hours}h / {u.monthly_capacity_hours}h</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Project Profitability Table ─────────────────────────────────────────────

const ProjectProfitabilityTable = ({ data }) => {
  if (!data?.length) return null;
  const withHours = data.filter(p => p.hours_this_month > 0);
  if (!withHours.length) return null;

  return (
    <div className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
      <div className="p-6 border-b-2 border-black flex items-center justify-between">
        <h2 className="font-oswald text-xl font-bold uppercase tracking-tight flex items-center gap-2">
          <TrendingUp size={20} />
          Ziskovost projektů — {getMonthName(parseInt(MONTH_NOW.split('-')[1]))} {MONTH_NOW.split('-')[0]}
        </h2>
        <Link to="/projects" className="font-oswald text-sm uppercase tracking-wide text-gray-500 hover:text-black flex items-center gap-1">
          Všechny projekty <ArrowRight size={14} />
        </Link>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-black bg-gray-50">
              <th className="text-left p-4 font-oswald text-xs uppercase tracking-wide">Projekt</th>
              <th className="text-left p-4 font-oswald text-xs uppercase tracking-wide hidden sm:table-cell">Klient</th>
              <th className="text-right p-4 font-oswald text-xs uppercase tracking-wide">Hodiny</th>
              <th className="text-right p-4 font-oswald text-xs uppercase tracking-wide hidden md:table-cell">Tržby</th>
              <th className="text-right p-4 font-oswald text-xs uppercase tracking-wide hidden md:table-cell">Náklady</th>
              <th className="text-right p-4 font-oswald text-xs uppercase tracking-wide">Zisk</th>
              <th className="text-right p-4 font-oswald text-xs uppercase tracking-wide">Marže</th>
            </tr>
          </thead>
          <tbody>
            {withHours.map((p, i) => (
              <tr key={p.id} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                <td className="p-4">
                  <Link to={`/projects/${p.id}`} className="font-oswald font-bold hover:text-[#006CB6] transition-colors">
                    {p.name}
                  </Link>
                </td>
                <td className="p-4 font-serif text-sm text-gray-500 hidden sm:table-cell">{p.client_name}</td>
                <td className="p-4 text-right font-oswald font-bold">{p.hours_this_month}h</td>
                <td className="p-4 text-right font-serif text-sm hidden md:table-cell">{formatCurrency(p.revenue)}</td>
                <td className="p-4 text-right font-serif text-sm text-gray-500 hidden md:table-cell">{formatCurrency(p.cost_this_month)}</td>
                <td className="p-4 text-right font-oswald font-bold" style={{ color: getProfitColor(p.profit) }}>
                  {formatCurrency(p.profit)}
                </td>
                <td className="p-4 text-right">
                  {p.margin_percent !== null ? (
                    <span className="inline-block px-2 py-0.5 font-oswald text-sm font-bold text-white"
                      style={{ backgroundColor: getMarginColor(p.margin_percent) }}>
                      {p.margin_percent}%
                    </span>
                  ) : (
                    <span className="text-gray-400 font-serif text-sm">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Quick Action Dialogs ────────────────────────────────────────────────────

const NewTaskDialog = ({ open, onClose, projects, onCreated }) => {
  const [title, setTitle] = useState('');
  const [projectId, setProjectId] = useState('');
  const [deadline, setDeadline] = useState('');
  const [saving, setSaving] = useState(false);

  const activeProjects = projects.filter(p => p.status === 'active');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !projectId) return;
    setSaving(true);
    try {
      await createTask({ title: title.trim(), project_id: projectId, deadline: deadline || null, status: 'todo', priority: 'medium' });
      toast.success('Úkol vytvořen');
      setTitle(''); setProjectId(''); setDeadline('');
      onCreated?.();
      onClose();
    } catch {
      toast.error('Chyba při vytváření úkolu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="border-2 border-black rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-oswald uppercase text-xl">Nový úkol</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label className="font-oswald uppercase text-xs tracking-wide">Název *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Název úkolu" className="border-2 border-gray-200 rounded-none" autoFocus />
          </div>
          <div className="space-y-1">
            <Label className="font-oswald uppercase text-xs tracking-wide">Projekt *</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="border-2 border-gray-200 rounded-none">
                <SelectValue placeholder="Vyberte projekt" />
              </SelectTrigger>
              <SelectContent>
                {activeProjects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="font-oswald uppercase text-xs tracking-wide">Deadline (volitelně)</Label>
            <Input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} className="border-2 border-gray-200 rounded-none" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-2">Zrušit</Button>
            <Button type="submit" disabled={saving || !title.trim() || !projectId} className="rounded-none bg-black text-white border-2 border-black hover:bg-gray-900">
              {saving ? 'Ukládám...' : 'Vytvořit'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const NewProjectDialog = ({ open, onClose, clients, onCreated }) => {
  const [name, setName] = useState('');
  const [clientId, setClientId] = useState('');
  const [revenue, setRevenue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !clientId) return;
    setSaving(true);
    try {
      await createProject({ name: name.trim(), client_id: clientId, status: 'active', estimated_revenue: revenue ? parseFloat(revenue) : 0 });
      toast.success('Projekt vytvořen');
      setName(''); setClientId(''); setRevenue('');
      onCreated?.();
      onClose();
    } catch {
      toast.error('Chyba při vytváření projektu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="border-2 border-black rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-oswald uppercase text-xl">Nový projekt</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label className="font-oswald uppercase text-xs tracking-wide">Název *</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Název projektu" className="border-2 border-gray-200 rounded-none" autoFocus />
          </div>
          <div className="space-y-1">
            <Label className="font-oswald uppercase text-xs tracking-wide">Klient *</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="border-2 border-gray-200 rounded-none">
                <SelectValue placeholder="Vyberte klienta" />
              </SelectTrigger>
              <SelectContent>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="font-oswald uppercase text-xs tracking-wide">Odhadovaná hodnota (Kč)</Label>
            <Input type="number" value={revenue} onChange={e => setRevenue(e.target.value)} placeholder="0" className="border-2 border-gray-200 rounded-none" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-2">Zrušit</Button>
            <Button type="submit" disabled={saving || !name.trim() || !clientId} className="rounded-none bg-black text-white border-2 border-black hover:bg-gray-900">
              {saving ? 'Ukládám...' : 'Vytvořit'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const NewClientDialog = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await createClient({ name: name.trim(), industry: industry || null, contact_email: email || null });
      toast.success('Klient vytvořen');
      setName(''); setIndustry(''); setEmail('');
      onCreated?.();
      onClose();
    } catch {
      toast.error('Chyba při vytváření klienta');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="border-2 border-black rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-oswald uppercase text-xl">Nový klient</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label className="font-oswald uppercase text-xs tracking-wide">Název *</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Název klienta" className="border-2 border-gray-200 rounded-none" autoFocus />
          </div>
          <div className="space-y-1">
            <Label className="font-oswald uppercase text-xs tracking-wide">Obor</Label>
            <Input value={industry} onChange={e => setIndustry(e.target.value)} placeholder="Např. E-commerce" className="border-2 border-gray-200 rounded-none" />
          </div>
          <div className="space-y-1">
            <Label className="font-oswald uppercase text-xs tracking-wide">Kontaktní e-mail</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="kontakt@firma.cz" className="border-2 border-gray-200 rounded-none" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-2">Zrušit</Button>
            <Button type="submit" disabled={saving || !name.trim()} className="rounded-none bg-[#006CB6] text-white border-2 border-[#006CB6] hover:bg-blue-700">
              {saving ? 'Ukládám...' : 'Vytvořit'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Dashboard ──────────────────────────────────────────────────────────

const Dashboard = () => {
  const { user, hasPermission } = useAuth();
  const { isRunning, startTimer } = useTimer();
  const navigate = useNavigate();

  const isAdmin = user?.is_admin;

  const [stats, setStats] = useState({ clients: 0, projects: 0, tasks: 0 });
  const [revenueData, setRevenueData] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [userCapacity, setUserCapacity] = useState([]);
  const [projectProfitability, setProjectProfitability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);

  // Dialog state
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const handleAlertClick = (alert) => {
    if (alert.entity_type === 'project' && alert.entity_id) navigate(`/projects/${alert.entity_id}`);
    else if (alert.entity_type === 'user') navigate('/users');
    else if (alert.entity_type === 'time_entries') navigate('/time-tracker');
  };

  const refreshData = async () => {
    try {
      const [upcomingRes, projectsRes] = await Promise.all([
        hasPermission('tasks') ? getUpcomingTasks() : Promise.resolve({ data: [] }),
        hasPermission('projects') ? getProjects() : Promise.resolve({ data: [] }),
      ]);
      setUpcomingTasks(upcomingRes.data || []);
      setProjects(projectsRes.data || []);
    } catch {}
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsRes, projectsRes, tasksRes] = await Promise.all([
          hasPermission('clients') ? getClients() : Promise.resolve({ data: [] }),
          hasPermission('projects') ? getProjects() : Promise.resolve({ data: [] }),
          hasPermission('tasks') ? getTasks() : Promise.resolve({ data: [] }),
        ]);

        setClients(clientsRes.data || []);
        setProjects(projectsRes.data || []);
        setStats({
          clients: clientsRes.data.length,
          projects: projectsRes.data.length,
          tasks: tasksRes.data.length,
        });

        if (hasPermission('tasks')) {
          try { const r = await getUpcomingTasks(); setUpcomingTasks(r.data || []); } catch {}
        }

        if (hasPermission('revenue')) {
          try {
            const r = await getRevenueSummary(new Date().getFullYear());
            setRevenueData(r.data.map(d => ({ name: getMonthName(d.month), odhad: d.estimated, realita: d.actual })));
          } catch {}
        }

        if (isAdmin) {
          try { const r = await getDashboardAlerts(); setAlerts(r.data || []); } catch {}
          try { const r = await getMonthlySummary(); setMonthlySummary(r.data); } catch {}
          try { const r = await getUserCapacity(); setUserCapacity(r.data || []); } catch {}
          try { const r = await getProjectProfitability(); setProjectProfitability(r.data || []); } catch {}
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [hasPermission, isAdmin]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="font-oswald text-xl uppercase tracking-wide">Načítání...</div>
      </div>
    );
  }

  const activeProjects = projects.filter(p => p.status === 'active');

  return (
    <div className="space-y-8" data-testid="dashboard">

      {/* Header */}
      <div>
        <h1 className="font-oswald text-4xl font-bold uppercase tracking-tight">Dashboard</h1>
        <p className="font-serif text-gray-600 mt-1">Vítejte zpět, {user?.name}</p>
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-yellow-400 to-yellow-300 border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Zap size={24} className="text-black" />
            <span className="font-oswald text-lg font-bold uppercase tracking-tight">Rychlé akce</span>
          </div>
          <div className="flex flex-wrap gap-3">
            {/* Timer select — active projects only */}
            {hasPermission('time_tracking') && activeProjects.length > 0 && (
              <select
                disabled={isRunning}
                className="px-3 py-2 bg-white border-2 border-black font-serif text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                onChange={(e) => { if (e.target.value) { startTimer({ project_id: e.target.value, project_name: activeProjects.find(p => p.id === e.target.value)?.name }); e.target.value = ''; } }}
              >
                <option value="">⏱ Spustit timer…</option>
                {activeProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}

            {/* New task */}
            {hasPermission('tasks') && (
              <button
                onClick={() => setTaskDialogOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white font-oswald uppercase text-sm tracking-wide border-2 border-black hover:bg-gray-900 transition-all"
              >
                <Plus size={16} /> Nový úkol
              </button>
            )}

            {/* New project */}
            {isAdmin && hasPermission('projects') && (
              <button
                onClick={() => setProjectDialogOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-black font-oswald uppercase text-sm tracking-wide border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
              >
                <FolderKanban size={16} /> Nový projekt
              </button>
            )}

            {/* New client */}
            {isAdmin && hasPermission('clients') && (
              <button
                onClick={() => setClientDialogOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#006CB6] text-white font-oswald uppercase text-sm tracking-wide border-2 border-black hover:bg-blue-700 transition-all"
              >
                <Users size={16} /> Nový klient
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Admin: Alerts + Forecast */}
      {isAdmin && (alerts.length > 0 || monthlySummary?.missing_data) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <AlertBox alerts={alerts} missingData={monthlySummary?.missing_data} onAlertClick={handleAlertClick} />
          </div>
          {monthlySummary && (
            <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <h2 className="font-oswald text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
                <Target size={20} /> Forecast {getMonthName(parseInt(monthlySummary.month?.split('-')[1]))}
              </h2>
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 uppercase font-oswald">Očekávaný zisk</div>
                  <div className="text-3xl font-bold font-oswald" style={{ color: getProfitColor(monthlySummary.forecast_profit) }}>
                    {monthlySummary.data_complete ? formatCurrency(monthlySummary.forecast_profit) : '—'}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-200">
                  <div>
                    <div className="text-xs text-gray-500 font-oswald">Tržby (odhad)</div>
                    <div className="text-lg font-bold">{formatCurrency(monthlySummary.estimated_revenue)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 font-oswald">Náklady (odhad)</div>
                    <div className="text-lg font-bold">{monthlySummary.data_complete ? formatCurrency(monthlySummary.estimated_costs) : '—'}</div>
                  </div>
                </div>
                <div className="pt-3 border-t border-gray-200">
                  <div className="text-xs text-gray-500 font-oswald">Marže</div>
                  <div className="text-xl font-bold" style={{ color: getMarginColor(monthlySummary.margin_percent) }}>
                    {monthlySummary.margin_percent}%
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Admin: Financial KPIs */}
      {isAdmin && monthlySummary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard title="Skutečné tržby" value={formatCurrency(monthlySummary.actual_revenue)} subtitle="tento měsíc" icon={DollarSign} color="#22c55e" />
          <KPICard title="Skutečné náklady" value={formatCurrency(monthlySummary.real_costs)} subtitle={`ext.: ${formatCurrency(monthlySummary.external_costs)}`} icon={TrendingDown} color="#ef4444" />
          <KPICard title="Skutečný zisk" value={formatCurrency(monthlySummary.actual_profit)} subtitle="realizováno" icon={TrendingUp} color={getProfitColor(monthlySummary.actual_profit)} />
          <KPICard
            title="Využití kapacity"
            value={monthlySummary.total_capacity_hours > 0 ? `${monthlySummary.capacity_usage_percent}%` : '—'}
            subtitle={`${monthlySummary.tracked_hours}h / ${monthlySummary.total_capacity_hours}h`}
            icon={Activity}
            color={monthlySummary.capacity_usage_percent > 100 ? '#ef4444' : monthlySummary.capacity_usage_percent > 80 ? '#f59e0b' : '#22c55e'}
          />
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {hasPermission('clients') && <StatCard title="Klienti" value={stats.clients} icon={Users} link="/clients" color="#F4E600" />}
        {hasPermission('projects') && <StatCard title="Projekty" value={stats.projects} icon={FolderKanban} link="/projects" color="#006CB6" />}
        {hasPermission('tasks') && <StatCard title="Úkoly" value={stats.tasks} icon={CheckSquare} link="/tasks" color="#000000" />}
      </div>

      {/* Admin: Project Profitability Table */}
      {isAdmin && projectProfitability.length > 0 && (
        <ProjectProfitabilityTable data={projectProfitability} />
      )}

      {/* Admin: Capacity + Revenue Chart */}
      {isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CapacityBar users={userCapacity} />
          {hasPermission('revenue') && revenueData.length > 0 && (
            <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-6">
                Tržby {new Date().getFullYear()}
              </h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueData}>
                    <XAxis dataKey="name" tick={{ fontFamily: 'Oswald', fontSize: 10 }} />
                    <YAxis tick={{ fontFamily: 'Source Serif 4', fontSize: 12 }} tickFormatter={(v) => `${v / 1000}k`} />
                    <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ fontFamily: 'Source Serif 4' }} />
                    <Legend wrapperStyle={{ fontFamily: 'Oswald', textTransform: 'uppercase', fontSize: '12px' }} />
                    <Bar dataKey="odhad" fill="#006CB6" name="Odhad" />
                    <Bar dataKey="realita" fill="#F4E600" name="Realita" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Non-admin: Revenue chart */}
      {!isAdmin && hasPermission('revenue') && revenueData.length > 0 && (
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-6">Tržby {new Date().getFullYear()}</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueData}>
                <XAxis dataKey="name" tick={{ fontFamily: 'Oswald', fontSize: 10 }} />
                <YAxis tick={{ fontFamily: 'Source Serif 4', fontSize: 12 }} tickFormatter={(v) => `${v / 1000}k`} />
                <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ fontFamily: 'Source Serif 4' }} />
                <Legend wrapperStyle={{ fontFamily: 'Oswald', textTransform: 'uppercase', fontSize: '12px' }} />
                <Bar dataKey="odhad" fill="#006CB6" name="Odhad" />
                <Bar dataKey="realita" fill="#F4E600" name="Realita" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Upcoming Tasks */}
      {upcomingTasks.length > 0 && (
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-oswald text-xl font-bold uppercase tracking-tight flex items-center gap-2">
              <Calendar size={20} className="text-[#006CB6]" /> Nejbližší úkoly
            </h2>
            <Link to="/tasks" className="font-oswald text-sm uppercase tracking-wide text-gray-500 hover:text-black flex items-center gap-1">
              Všechny úkoly <ArrowRight size={14} />
            </Link>
          </div>
          <div className="space-y-2">
            {upcomingTasks.slice(0, 5).map((task) => {
              const overdue = new Date(task.deadline) < new Date();
              return (
                <div key={task.id} onClick={() => { setSelectedTask(task); setDetailDialogOpen(true); }}
                  className={`p-4 border-2 cursor-pointer transition-colors hover:border-black ${overdue ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-serif font-medium">{task.title}</div>
                      <div className="text-sm text-gray-500 font-serif mt-0.5">{task.project_name} · {task.client_name}</div>
                    </div>
                    <div className={`flex items-center gap-1 text-sm font-oswald ml-4 ${overdue ? 'text-red-600 font-bold' : 'text-gray-600'}`}>
                      <Calendar size={14} /> {task.deadline}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Dialogs */}
      <NewTaskDialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} projects={projects} onCreated={refreshData} />
      <NewProjectDialog open={projectDialogOpen} onClose={() => setProjectDialogOpen(false)} clients={clients} onCreated={refreshData} />
      <NewClientDialog open={clientDialogOpen} onClose={() => setClientDialogOpen(false)} onCreated={refreshData} />
      <TaskDetailDialog task={selectedTask} open={detailDialogOpen} onOpenChange={setDetailDialogOpen} onTaskUpdated={refreshData} />
    </div>
  );
};

export default Dashboard;